import React, { useCallback, useEffect, useState } from "react";

import { assignCreditsToPartner, getAssignableFarmFieldModules, getPartnerCreditAssignments } from "services";

import { useCancelAsyncOnUnmount } from "utils";

import { AdminTable, Col, InsightCard, PageSpin, Row, Switch, Message as message } from "components";

const itemsPerPage = 250;

// TODO: think about reusing one component for AssignModuleCredits and AssignModuleCreditsToSinglePartner
export const AssignModuleCreditsToSinglePartner = ({ partner }) => {
  const signal = useCancelAsyncOnUnmount()();
  const [farmFieldModules, setFarmFieldModules] = useState([]);
  const [loadingInitialFieldModules, setLoadingInitialFieldModules] = useState(true);
  const [loadingAssigningCredits, setLoadingAssigningCredits] = useState(false);
  const [loadingCredit, setLoadingCredit] = useState(true);
  const [assignedCredits, setAssignedCredits] = useState(0);
  const [assignedModules, setAssignedModules] = useState([]);
  const [isInsetting, setIsInsetting] = useState(false);
  const [fieldModulePage, setFieldModulePage] = useState(0);

  const loadFarmFieldModules = useCallback(() => {
    getAssignableFarmFieldModules({
      page: fieldModulePage,
      itemsPerPage,
      signal,
    })
      .then((res) => {
        setFarmFieldModules((prev) => [
          ...prev,
          ...res.modules.filter(
            (fieldModule) => ![fieldModule.category1, fieldModule.category2, fieldModule.category3, fieldModule.category4].every((item) => item === 0)
          ),
        ]);

        if (res.count > fieldModulePage * itemsPerPage && res.count > itemsPerPage) {
          setFieldModulePage(fieldModulePage + 1);
        }
      })
      .finally(() => setLoadingInitialFieldModules(false));
  }, [signal, fieldModulePage]);

  const getCreditAssignments = useCallback(() => {
    setLoadingCredit(true);
    getPartnerCreditAssignments({
      id: partner.id,
      signal,
    }).then((res) => {
      setLoadingCredit(false);
      setAssignedCredits(res.filter(({ program }) => program === "Soil+").reduce((a, b) => a + (b.category1 + b.category2 + b.category3 + b.category4), 0));
    });
  }, [partner, signal]);

  const complete = (v) => {
    if (isInsetting && partner.id !== v.farmPartnerId) {
      message({
        content: "You can only assign credits as insetting, when the farmer is assigned to the partner",
        icon: "gremlin",
      });
      return;
    }

    const credits = {
      category1: v.category1,
      category2: v.category2,
      category3: v.category3,
      category4: v.category4,
    };

    const totalAssignedCredits = v.category1 + v.category2 + v.category3 + v.category4;

    setLoadingAssigningCredits(true);
    setAssignedCredits(assignedCredits + totalAssignedCredits);

    assignCreditsToPartner({
      credits,
      farmFieldMethodOrModuleId: v.fieldModuleId,
      partnerId: partner.id,
      creditType: isInsetting ? "Insetting" : "Offsetting",
      farmId: v.farmId,
      fieldId: v.fieldId,
      completed: v.completed,
      year: v.year,
      type: "Soil+",
      signal,
    }).then(() => {
      setLoadingAssigningCredits(false);
      const totalAssignedModules = [...assignedModules, v.fieldModuleId];
      const availableFieldModules = farmFieldModules.filter((fieldModule) => !totalAssignedModules.includes(fieldModule.id));
      setAssignedModules(totalAssignedModules);
      setFarmFieldModules(availableFieldModules);
      message({
        content: "The gremlins accepted your sacrifice and will consume it! Nom nom nom",
        icon: "gremlin",
      });
    });
  };

  useEffect(() => {
    loadFarmFieldModules();
  }, [loadFarmFieldModules]);

  useEffect(() => {
    getCreditAssignments();
  }, [getCreditAssignments]);

  return (
    <>
      <Row>
        <Col sm={8}>
          <InsightCard label="Partner credit purchases" loading={loadingCredit}>
            {partner.purchasedCredits}
          </InsightCard>
        </Col>
        <Col sm={8}>
          <InsightCard label="Assigned Soil+ credits" loading={loadingCredit}>
            {+assignedCredits.toFixed(4)}
          </InsightCard>
        </Col>
        <Col sm={8}>
          <InsightCard label="Remaining" loading={loadingCredit}>
            {+(partner.purchasedCredits - assignedCredits).toFixed(4)}
          </InsightCard>
        </Col>
      </Row>
      <Row>
        <Col sm={12}>
          <p className="paragraph-s red-text">
            Partial credit assignments are not supported on this page. Clicking the "+" button will generate a credit assignment for the entire available
            amount.
          </p>
        </Col>
        <Col sm={12} className="right">
          <span className="margin-right-50">Assign credits as Insetting</span>
          <Switch checked={isInsetting} onClick={(v) => setIsInsetting(v)} />
        </Col>
      </Row>

      {loadingInitialFieldModules || loadingCredit ? (
        <PageSpin fullHeight={false} />
      ) : (
        <AdminTable
          dataSource={farmFieldModules?.map(({ id, ...rest }) => ({ fieldModuleId: id, ...rest }))}
          loading={loadingAssigningCredits}
          config={{
            actions: [
              {
                icon: !loadingCredit ? "klim/plus" : "",
                func: complete,
                tooltip: "Assign these credits",
              },
            ],
            exclude: [
              "receivedPoints",
              "moduleId",
              "assignedCredits",
              "methodId",
              "completed",
              "fieldId",
              "fieldName",
              "farmPartnerId",
              "assignedCreditsCategory1",
              "assignedCreditsCategory2",
              "assignedCreditsCategory3",
              "assignedCreditsCategory4",
              "createdAt",
            ],
          }}
        />
      )}
    </>
  );
};
